/**
 * Tras GET operaciones/snat-ticket/context, aplica defaults desde variables de entorno
 * y desde token_resolved_* (query client_token / project_token validados en servidor).
 *
 * Variables (Vue CLI) — `VUE_APP_SNAT_DEFAULT_CLIENT_TOKEN` y `VUE_APP_SNAT_DEFAULT_PROJECT_TOKEN`
 * deben copiarse en soft_dev (Cliente/Proyecto) → campos `snat_embed_client_token` / `snat_embed_project_token`
 * (misma cadena, alta entropía; p. ej. `python -c "import secrets; print(secrets.token_urlsafe(48))"`).
 *
 * Prioridad de resolución:
 *   1) token_resolved_client_id / token_resolved_project_id (context API, vía ?client_token&project_token=…)
 *   2) VUE_APP_SNAT_DEFAULT_CLIENT_ID / VUE_APP_SNAT_DEFAULT_PROJECT_ID
 *   3) VUE_APP_SNAT_DEFAULT_CLIENT_KEY / VUE_APP_SNAT_DEFAULT_PROJECT_KEY (nombres, con fuzzy match)
 *
 * Ver también `VUE_APP_SNAT_DEPLOY_ENV` y `snatTicketPayloadExtras.js`.
 */

function norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * @param {object} form
 * @param {Array} clients
 * @param {Array} projects
 * @param {object} [contextData] — p. ej. `data` del GET context (token_resolved_*)
 */
export function applySnatTicketEnvDefaults(form, clients, projects, contextData = null) {
  const clist = Array.isArray(clients) ? clients : [];
  const plist = Array.isArray(projects) ? projects : [];
  const ctx = contextData && typeof contextData === 'object' ? contextData : {};

  const rawTc = ctx.token_resolved_client_id;
  const rawTp = ctx.token_resolved_project_id;
  const tCid = rawTc != null && rawTc !== '' ? parseInt(String(rawTc), 10) : NaN;
  const tPid = rawTp != null && rawTp !== '' ? parseInt(String(rawTp), 10) : NaN;
  if (!Number.isNaN(tCid) && tCid > 0 && clist.some((c) => Number(c.id) === tCid)) {
    form.client_id = tCid;
  }
  if (!Number.isNaN(tPid) && tPid > 0) {
    const p = plist.find((x) => Number(x.id) === tPid);
    if (p) {
      form.client_id = Number(p.client_id);
      form.project_id = tPid;
    }
  }

  const rawCid = String(process.env.VUE_APP_SNAT_DEFAULT_CLIENT_ID || '').trim();
  const rawPid = String(process.env.VUE_APP_SNAT_DEFAULT_PROJECT_ID || '').trim();
  const idClient = parseInt(rawCid, 10);
  const idProject = parseInt(rawPid, 10);
  const keyClient = norm(process.env.VUE_APP_SNAT_DEFAULT_CLIENT_KEY);
  const keyProject = norm(process.env.VUE_APP_SNAT_DEFAULT_PROJECT_KEY);

  if (form.client_id == null || form.client_id === '') {
    if (!Number.isNaN(idClient) && idClient > 0) {
      if (clist.some((c) => Number(c.id) === idClient)) {
        form.client_id = idClient;
      }
    } else if (keyClient) {
      const c =
        clist.find((x) => norm(x.name) === keyClient) || clist.find((x) => norm(x.name).includes(keyClient));
      if (c) form.client_id = Number(c.id);
    }
  }

  if (!form.client_id) return;

  const byClient = plist.filter((p) => Number(p.client_id) === Number(form.client_id));

  if (form.project_id == null || form.project_id === '') {
    if (!Number.isNaN(idProject) && idProject > 0) {
      const p = byClient.find((x) => Number(x.id) === idProject);
      if (p) form.project_id = idProject;
    } else if (keyProject) {
      const p =
        byClient.find((x) => norm(x.name) === keyProject) ||
        byClient.find((x) => norm(x.name).includes(keyProject));
      if (p) form.project_id = Number(p.id);
    }
  }
}

/**
 * @returns {{ client: string, project: string }} tokens de build; vacíos si no hay env.
 * Props del componente pueden reemplazar (SnatTicketButton: embedClientToken, embedProjectToken).
 */
export function getSnatEmbedTokenPair(embedClientTokenProp, embedProjectTokenProp) {
  return {
    client: String(
      (embedClientTokenProp != null && String(embedClientTokenProp).trim()) ||
        process.env.VUE_APP_SNAT_DEFAULT_CLIENT_TOKEN ||
        '',
    ).trim(),
    project: String(
      (embedProjectTokenProp != null && String(embedProjectTokenProp).trim()) ||
        process.env.VUE_APP_SNAT_DEFAULT_PROJECT_TOKEN ||
        '',
    ).trim(),
  };
}
